"use client";
import {
  Button,
  makeStyles,
  SearchBox,
  tokens,
} from "@fluentui/react-components";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useColumnWidths } from "../../hooks/useColumnWidths";
import {
  useGetAllChapterImages,
  useGetImageFields,
} from "../../hooks/useImages";
import { ClientRoutes } from "../../models/ClientRoutes";
import { Image } from "../../models/Image";
import { FieldColumnSizes } from "../../services/GenericDataGridService";
import { getImageColumnsFromColumnOrder } from "../../services/ViewService";
import { ConstrainedContent } from "../layout/ConstrainedContent";
import GenericDataGrid from "../list/GenericDataGrid";
import { PublishedListSkeleton } from "../skeletons/PublishedListSkeleton";

const useStyles = makeStyles({
  header: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: tokens.spacingVerticalM,
  },
  searchInput: {
    width: "300px",
  },
});

const ChapterImagesList = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: images, isLoading } = useGetAllChapterImages();
  const { data: viewDefinition, isLoading: viewDefinitionLoading } =
    useGetImageFields();
  const [query, setQuery] = useState<string>("");

  const {
    columnSizingOptions: persistedColumnSizingOptions,
    handleColumnResize,
    gridKey,
  } = useColumnWidths("images:chapter", FieldColumnSizes);

  const handleEdit = useCallback(
    (imageId: string) => {
      navigate(`${ClientRoutes.MediaChapterEdit}/${imageId}`);
    },
    [navigate]
  );

  const columns = useMemo(() => {
    if (viewDefinitionLoading || !viewDefinition) {
      return [];
    }

    return getImageColumnsFromColumnOrder(
      viewDefinition.standardFields,
      viewDefinition.fields,
      [], // No activity fields for chapter images
      viewDefinition.thumbnailFieldOrder ?? [],
      handleEdit,
      false,
      false
    );
  }, [viewDefinition, viewDefinitionLoading, handleEdit]);

  const filteredImages = useMemo<Image[]>(() => {
    if (!images || isLoading) {
      return [];
    }

    if (!query) {
      return images;
    }

    const normalizedQuery = query.toLowerCase();
    return images.filter((image) => {
      const searchFields = [
        image.title || "",
        image.imageId || "",
        image.fileName || "",
      ];

      return searchFields.some((field) =>
        field.toLowerCase().includes(normalizedQuery)
      );
    });
  }, [query, images, isLoading]);

  const selectionResetKey = useMemo(() => {
    return filteredImages
      .map((image) => image.imageId || image.versionId)
      .join("|");
  }, [filteredImages]);

  const handleCreateImage = useCallback(() => {
    navigate(ClientRoutes.MediaChapterCreate);
  }, [navigate]);

  if (isLoading || viewDefinitionLoading) {
    return <PublishedListSkeleton />;
  }

  return (
    <div>
      <ConstrainedContent>
        <div className={classes.header}>
          <Button appearance="primary" onClick={handleCreateImage}>
            {t("uploadChapterImage")}
          </Button>
          <SearchBox
            key={`search-${query === "" ? "empty" : "has"}`}
            className={classes.searchInput}
            placeholder={t("searchImage")}
            value={query}
            onChange={(_e, data) => setQuery(data.value)}
          />
        </div>
      </ConstrainedContent>

      <GenericDataGrid<Image>
        key={`${gridKey}-${selectionResetKey}`}
        items={filteredImages}
        getRowId={(image) => image.imageId || image.versionId}
        columns={columns}
        columnSizingOptions={persistedColumnSizingOptions}
        onColumnResize={handleColumnResize}
        enableSelection
      />
    </div>
  );
};

export default ChapterImagesList;
